/** Recording content adapter. Playback position/Full–Detail state belongs to the
 * retained host, never to a Resource payload or its original command receipt. */
import type { DraftContext } from '@/features/coach-review/composerDrafts';
import { emptyDrawing, loadDrawing, saveDrawing, type DrawingV2 } from '@/lib/canvas/telestrator-schema';
import { feedbackProseLength } from '@/lib/feedbackAuthoringContinuation';
import { resourceDrawingWire, resourceSaveBodySchema } from '@/lib/resourceEditorPayload';
import type { ResourceEditorReturnTarget } from '@/lib/resourceEditorContinuation';
import type { ResourceCommand, ResourceDetailResponse, ResourcePin, ResourceRecordingDescriptor, SaveResourceRequest } from '@/lib/types';
import { createResourceEditorSession, type ResourceEditorAdapter, type ResourceEditorInitial } from './resourceEditorSessionCore';
import { editableResourceLabel } from './resourceText';

export type ResourceVideoKind='moment'|'clip';
export type ResourceVideoValue={
  kind:ResourceVideoKind;label:string;recording:ResourceRecordingDescriptor|null;drawing:DrawingV2;
  /** Preserve a valid unchanged drawing's exact original representation. */
  drawingWire:Record<string,unknown>|null;
  timestampSeconds:number;window:{start:number;end:number};derivedFrom:ResourcePin|null;
  provenanceUnavailable:boolean;
};
const operation='00000000-0000-4000-8000-000000000001';
function stable(value:unknown):string{return JSON.stringify(value,(_key,v)=>v&&typeof v==='object'&&!Array.isArray(v)
  ?Object.fromEntries(Object.entries(v).sort(([a],[b])=>a.localeCompare(b))):v);}
const drawingKey=(value:DrawingV2)=>stable(saveDrawing(value));
function wireDrawing(value:ResourceVideoValue){
  return drawingKey(value.drawing)===drawingKey(loadDrawing(value.drawingWire))?value.drawingWire
    :value.drawing.objects.length?saveDrawing(value.drawing):null;
}
function payload(value:ResourceVideoValue):SaveResourceRequest['payload'] {
  if(!value.recording)throw new Error('resource_editor_recording_required');
  if(value.provenanceUnavailable)throw new Error('resource_editor_origin_unavailable');
  const {recording_id,recording_generation,match_id,slot}=value.recording;
  const common={schema_version:1 as const,recording_id,recording_generation,match_id,slot,drawing:wireDrawing(value)};
  return value.kind==='moment'?{...common,timestamp_seconds:value.timestampSeconds}
    :{...common,start_seconds:value.window.start,end_seconds:value.window.end,derived_from:value.derivedFrom};
}
function fromPayload(kind:ResourceVideoKind,data:SaveResourceRequest['payload'],label:string,unavailable=false):ResourceVideoValue {
  if(!('recording_id' in data))throw new Error('resource_editor_kind_mismatch');
  if(kind==='moment'&&!('timestamp_seconds' in data)||kind==='clip'&&!('start_seconds' in data))throw new Error('resource_editor_kind_mismatch');
  const {recording_id,recording_generation,match_id,slot}=data;
  const wire=data.drawing??null;
  if(!resourceDrawingWire(wire))throw new Error('resource_editor_invalid_drawing');
  return {kind,label,recording:{schema_version:'resource-recording.v1',recording_id,recording_generation,match_id,slot},
    drawing:loadDrawing(wire),drawingWire:wire,timestampSeconds:'timestamp_seconds' in data?data.timestamp_seconds:0,
    window:'start_seconds' in data?{start:data.start_seconds,end:data.end_seconds}:{start:0,end:0},
    derivedFrom:'derived_from' in data?data.derived_from??null:null,provenanceUnavailable:unavailable};
}
function fromDetail(kind:ResourceVideoKind,detail:ResourceDetailResponse):ResourceVideoValue {
  const item=detail.resource;
  if(item.availability!=='available'||item.kind==='tactical_board'||item.kind!==kind)throw new Error('resource_editor_unavailable');
  // The name is version content (migration 0115): edit the selected revision's
  // own heading, so a Library-only rename never becomes what recipients see.
  // A server-default heading reads back as empty so it keeps following the time.
  const value=fromPayload(kind,item.payload,item.display_label,item.kind==='clip'&&item.provenance?.availability==='unavailable');
  return {...value,label:editableResourceLabel(kind,item.display_label,value)};
}
function fromCommand(kind:ResourceVideoKind,command:ResourceCommand):ResourceVideoValue|null {
  if(command.action!=='save'&&command.action!=='create')return null;
  if(command.body.kind!==kind)throw new Error('resource_editor_kind_mismatch');
  return fromPayload(kind,command.body.payload,command.body.label??'');
}
export function emptyResourceVideo(kind:ResourceVideoKind):ResourceVideoValue {
  return {kind,label:'',recording:null,drawing:emptyDrawing(),drawingWire:null,
    timestampSeconds:0,window:{start:0,end:0},derivedFrom:null,provenanceUnavailable:false};
}
function contentKey(value:ResourceVideoValue){
  return stable({kind:value.kind,label:value.label,recording:value.recording,drawing:saveDrawing(value.drawing),
    ...(value.kind==='moment'?{timestamp:value.timestampSeconds}:{window:value.window,derivedFrom:value.derivedFrom}),
    provenanceUnavailable:value.provenanceUnavailable});
}
function adapter(kind:ResourceVideoKind):ResourceEditorAdapter<ResourceVideoValue>{return {
  kind,fromDetail:detail=>fromDetail(kind,detail),fromCommand:command=>fromCommand(kind,command),
  validate(value){
    if(value.kind!==kind||feedbackProseLength(value.label)>160||!resourceDrawingWire(saveDrawing(value.drawing))
      ||!resourceDrawingWire(value.drawingWire))throw new Error('resource_editor_invalid_video');
    // Incomplete source/range is legitimate mounted editor state; no command
    // can capture it until payload admission below succeeds. Never clamp time.
    if(!Number.isFinite(value.timestampSeconds)||value.timestampSeconds<0||!Number.isFinite(value.window.start)
      ||!Number.isFinite(value.window.end)||value.window.start<0||value.window.end<0)throw new Error('resource_editor_invalid_video');
    return value;
  },
  changed:(a,b)=>contentKey(a)!==contentKey(b),
  payload(value){const result=payload(value);
    if(!resourceSaveBodySchema.safeParse({schema_version:'resource.v1',operation_id:operation,expected_version:0,kind,label:value.label,payload:result}).success)
      throw new Error('resource_editor_invalid_video');
    return result;
  },
  canPreserveContent:detail=>!(detail?.resource.availability==='available'&&detail.resource.kind==='clip'
    &&detail.resource.provenance?.availability==='unavailable'),
};}
export function createResourceVideoSession(context:DraftContext,entryId:string,kind:ResourceVideoKind,
  initial:ResourceEditorInitial<ResourceVideoValue>,returnTarget:ResourceEditorReturnTarget={kind:'standalone'}){
  return createResourceEditorSession(context,entryId,initial,adapter(kind),returnTarget);
}
export type ResourceVideoSession=ReturnType<typeof createResourceVideoSession>;
